/**
 * Ссылки на Telegram для входа.
 *
 * Зачем отдельный модуль. Адрес вида `https://t.me/<bot>?startapp=…` сначала
 * открывает страницу t.me и только с неё перебрасывает в приложение — человек
 * видит лишний экран, а на медленной связи ещё и ждёт его загрузку. Схема
 * `tg://` поднимает приложение сразу.
 *
 * Запасной адрес нужен ровно для одного случая: Telegram не установлен, схему
 * `tg://` обработать некому и переход молча не происходит. Тогда человеку
 * нужна ссылка на `https://t.me/…`, где ему предложат поставить приложение
 * или открыть веб-версию (Android уходит туда сам — `intent://`).
 *
 * Имя бота сюда приходит уже без `@` (его снимает TelegramLoginPollingService).
 */

/** Пара адресов: сначала пробуем прямой, при неудаче — запасной. */
export interface TelegramLinks {
  /** `tg://…` — поднимает приложение без промежуточной страницы. */
  deepLink: string
  /** `https://t.me/…` — если приложения нет. */
  webLink: string
}

/**
 * Мини-апп бота (`startapp`). Телефон уходит сюда и остаётся в Telegram:
 * кабинет открывается прямо в приложении, возвращаться в браузер не нужно.
 */
export function miniAppLinks(bot: string, startParam: string): TelegramLinks {
  const query = startParam ? `&startapp=${encodeURIComponent(startParam)}` : ''

  return {
    deepLink: `tg://resolve?domain=${encodeURIComponent(bot)}${query}`,
    webLink: startParam
      ? `https://t.me/${bot}?startapp=${encodeURIComponent(startParam)}`
      : `https://t.me/${bot}`,
  }
}

/**
 * Переписка с ботом (`start`). Так уходит десктоп: мини-апп на большом экране
 * открывать некуда, и вход заканчивается опросом челленджа в той же вкладке.
 */
export function botChatLinks(bot: string, startParam: string): TelegramLinks {
  const query = startParam ? `&start=${encodeURIComponent(startParam)}` : ''

  return {
    deepLink: `tg://resolve?domain=${encodeURIComponent(bot)}${query}`,
    webLink: startParam
      ? `https://t.me/${bot}?start=${encodeURIComponent(startParam)}`
      : `https://t.me/${bot}`,
  }
}

/** Чем открывается Telegram на этом устройстве (FLOW-V5 §3.3). */
export type TelegramPlatform = 'android' | 'ios' | 'desktop'

/**
 * Платформа по userAgent. Здесь, в отличие от раскладки кабинета, ширина
 * экрана не годится: `intent://` понимает только Android, а схема на iPad с
 * клавиатурой ведёт себя как на iPhone.
 */
export function telegramPlatform(
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  maxTouchPoints: number = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints ?? 0,
): TelegramPlatform {
  if (/Android/i.test(userAgent)) return 'android'
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios'
  // iPadOS притворяется маком — выдаёт его сенсорный экран.
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return 'ios'

  return 'desktop'
}

/**
 * Адрес, которым приложение открывается на этой платформе.
 *
 * Android — `intent://` со встроенным запасным адресом: нет Telegram — Chrome
 * сам уйдёт на t.me, без наших таймеров. Остальным — прямая схема `tg://`.
 */
export function telegramAppLink(links: TelegramLinks, platform: TelegramPlatform): string {
  if (platform !== 'android') return links.deepLink

  const path = links.deepLink.replace(/^tg:\/\//, '')

  return `intent://${path}#Intent;scheme=tg;package=org.telegram.messenger;`
    + `S.browser_fallback_url=${encodeURIComponent(links.webLink)};end`
}

/**
 * Уводит ТЕКУЩУЮ вкладку в приложение Telegram.
 *
 * FLOW-V5 Б3 — автоматического ухода на t.me по таймеру больше нет. Признак
 * «приложение не открылось — вкладка всё ещё видна» врал: на десктопе и в
 * iOS Safari вкладка остаётся на экране под системным диалогом «Открыть в
 * Telegram?», и человек, нажавший «Открыть», попадал ещё и на страницу t.me.
 * Запасной путь теперь — ссылка, которую человек нажимает сам: у окна
 * ожидания входа это «Открыть бота» и QR, у переноса — «Не открылся Telegram?».
 */
export function openTelegram(
  links: TelegramLinks,
  target: Window = window,
  platform: TelegramPlatform = telegramPlatform(),
): void {
  target.location.href = telegramAppLink(links, platform)
}
